import {
  type ServiceSizingPolicyEditProps,
  containerSizesByProfile,
  fetchActivePlans,
  fetchApp,
  fetchService,
  fetchServiceSizingPoliciesByServiceId,
  getContainerProfileFromType,
  modifyServiceSizingPolicy,
  scaleService,
  selectActiveMountPointsByServiceId,
  selectAppById,
  selectContainerProfilesForStack,
  selectEndpointsByServiceId,
  selectEnvironmentById,
  selectFirstActivePlan,
  selectManualScaleRecommendationByServiceId,
  selectPersistentDiskCostByService,
  selectServiceById,
  selectServiceSizingPolicyByServiceId,
  selectStackById,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { appActivityUrl } from "@app/routes";
import { DEFAULT_INSTANCE_CLASS, schema } from "@app/schema";
import { filterDecimalInput, filterIntegerInput } from "@app/string-utils";
import type { DeployServiceSizingPolicy, InstanceClass } from "@app/types";
import { type SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { type ValidResult, useValidator } from "../hooks";
import {
  Banner,
  ButtonIcon,
  ButtonLinkDocs,
  ContainerEfficiencySection,
  Disclosure,
  IconRefresh,
  KeyValueGroup,
  LastScaleBanner,
  ServicePricingCalc,
  tokens,
} from "../shared";
import {
  BannerMessages,
  Box,
  Button,
  FormGroup,
  Input,
  Select,
  type SelectOption,
} from "../shared";

const validators = {
  containerCount: (data: AppScaleProps) => {
    if (data.containerCount > 32) {
      return "Container count cannot exceed 32";
    }
  },
};

// Safely format a possibly-null numeric value to a fixed number of decimals.
// Falls back to the provided default when value is null/undefined/NaN.
const formatFixed = (
  value: number | null | undefined,
  decimalPlaces: number,
  fallback: number,
): string => {
  const n =
    typeof value === "number" && Number.isFinite(value) ? value : fallback;
  return n.toFixed(decimalPlaces);
};

const policyValidators = {
  ratios: (data: ServiceSizingPolicyEditProps) => {
    if (
      data.autoscaling === "vertical" &&
      data.memCpuRatioRThreshold < data.memCpuRatioCThreshold
    ) {
      return "Ratio for R must be larger than ratio for C";
    }
  },
  minContainers: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal") {
      if (data.minContainers === null || data.minContainers === undefined) {
        return "Minimum containers is required";
      }
      if (data.minContainers <= 0) {
        return "Minimum containers must be greater than 0";
      }
      if (data.maxContainers && data.minContainers > data.maxContainers) {
        return "Minimum containers must be less than maximum containers";
      }
    }
  },
  maxContainers: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal") {
      if (data.maxContainers === null || data.maxContainers === undefined) {
        return "Maximum containers is required";
      }
      if (data.maxContainers <= 0) {
        return "Maximum containers must be greater than 0";
      }
      if (data.minContainers && data.maxContainers < data.minContainers) {
        return "Maximum containers must be above minimum containers";
      }
    }
  },
  minCpuThreshold: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal") {
      if (
        data.minCpuThreshold === null ||
        data.minCpuThreshold === undefined ||
        Number.isNaN(data.minCpuThreshold)
      ) {
        return "Scale down CPU threshold is required";
      }
      if (data.minCpuThreshold <= 0 || data.minCpuThreshold > 1) {
        return "Scale down CPU threshold must be between 0 and 1";
      }
      if (
        data.maxCpuThreshold &&
        !Number.isNaN(data.maxCpuThreshold) &&
        data.minCpuThreshold >= data.maxCpuThreshold
      ) {
        return "Scale down CPU threshold must be less than scale up threshold";
      }
    }
  },
  maxCpuThreshold: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal") {
      if (
        data.maxCpuThreshold === null ||
        data.maxCpuThreshold === undefined ||
        Number.isNaN(data.maxCpuThreshold)
      ) {
        return "Scale up CPU threshold is required";
      }
      if (data.maxCpuThreshold <= 0 || data.maxCpuThreshold > 1) {
        return "Scale up CPU threshold must be between 0 and 1";
      }
      if (
        data.minCpuThreshold &&
        !Number.isNaN(data.minCpuThreshold) &&
        data.maxCpuThreshold <= data.minCpuThreshold
      ) {
        return "Scale up CPU threshold must be greater than scale down threshold";
      }
    }
  },
  scaleUpStep: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal" && data.scaleUpStep <= 0) {
      return "Scale up step must be greater than 0";
    }
  },
  scaleDownStep: (data: ServiceSizingPolicyEditProps) => {
    if (data.autoscaling === "horizontal" && data.scaleDownStep <= 0) {
      return "Scale down step must be greater than 0";
    }
  },
  metricLookbackSeconds: (data: ServiceSizingPolicyEditProps) => {
    if (data.metricLookbackSeconds < 130 || data.metricLookbackSeconds > 3600) {
      return "Metric lookback seconds must be between 130 and 3600";
    }
  },
  percentile: (data: ServiceSizingPolicyEditProps) => {
    if (data.percentile < 50 || data.percentile > 100) {
      return "Percentile must be between 50 and 100";
    }
  },
};

type AppScaleProps = {
  containerCount: number;
};

const PolicySummary = ({
  policy,
  title,
}: { policy: DeployServiceSizingPolicy; title: string }) => {
  const data = [];
  if (policy.scalingEnabled) {
    switch (policy.autoscaling) {
      case "horizontal":
        data.push(
          {
            key: "Minimum Containers",
            value: policy.minContainers?.toString() || "",
          },
          {
            key: "Maximum Containers",
            value: policy.maxContainers?.toString() || "",
          },
          {
            key: "Scale Down CPU Threshold",
            value: policy.minCpuThreshold?.toString() || "",
          },
          {
            key: "Scale Up CPU Threshold",
            value: policy.maxCpuThreshold?.toString() || "",
          },
        );
        if (policy.scaleUpStep > 1)
          data.push({
            key: "Scale Up Steps",
            value: policy.scaleUpStep.toString(),
          });
        if (policy.scaleDownStep > 1)
          data.push({
            key: "Scale Down Steps",
            value: policy.scaleDownStep.toString(),
          });
        break;
      case "vertical":
        data.push(
          { key: "Minimum Memory", value: policy.minimumMemory.toString() },
          {
            key: "Maximum Memory",
            value: policy.maximumMemory?.toString() || "Not set",
          },
        );
        break;
    }
  }

  let titleAddition = "";
  if (policy.scalingEnabled)
    titleAddition = ` - ${policy.autoscaling} autoscaling`;
  else titleAddition = " - Autoscaling Disabled";

  const isHAS = policy.scalingEnabled && policy.autoscaling === "horizontal";
  const minContainers = policy.minContainers ?? 0;

  return (
    <div>
      <h4 className={`${tokens.type.h4} capitalize`}>
        {`${title} Settings${titleAddition}`}
      </h4>
      <KeyValueGroup data={data} variant="horizontal-inline" />
      {isHAS && minContainers < 2 ? (
        <Banner className="mt-2" variant="warning">
          Warning: High-availability requires at least 2 containers
        </Banner>
      ) : null}
    </div>
  );
};

type AutoscalingTypeInp = "horizontal" | "vertical" | "disabled";

type PolicyErrors = { [key in keyof typeof policyValidators]: ValidResult };

type BlurErrors = Partial<Record<keyof PolicyInputValues, string>>;

type PolicyInputValues = {
  minCpuThreshold: string;
  maxCpuThreshold: string;
  memScaleUpThreshold: string;
  memScaleDownThreshold: string;
  memCpuRatioRThreshold: string;
  memCpuRatioCThreshold: string;
  percentile: string;
  minimumMemory: string;
  maximumMemory: string;
  minContainers: string;
  maxContainers: string;
  scaleUpStep: string;
  scaleDownStep: string;
  metricLookbackSeconds: string;
  postScaleUpCooldownSeconds: string;
  postScaleDownCooldownSeconds: string;
  postReleaseCooldownSeconds: string;
  useHorizontalScale: string;
};

const computeInitialInputValues = (
  policy: ServiceSizingPolicyEditProps,
): PolicyInputValues => ({
  minCpuThreshold: formatFixed(policy.minCpuThreshold, 2, 0.1),
  maxCpuThreshold: formatFixed(policy.maxCpuThreshold, 2, 0.9),
  memScaleUpThreshold: formatFixed(policy.memScaleUpThreshold, 2, 0.8),
  memScaleDownThreshold: formatFixed(policy.memScaleDownThreshold, 2, 0.2),
  memCpuRatioRThreshold: formatFixed(policy.memCpuRatioRThreshold, 1, 2.0),
  memCpuRatioCThreshold: formatFixed(policy.memCpuRatioCThreshold, 2, 1.0),
  percentile: String(policy.percentile ?? ""),
  minimumMemory: String(policy.minimumMemory ?? ""),
  maximumMemory:
    policy.maximumMemory != null ? String(policy.maximumMemory) : "",
  minContainers:
    policy.minContainers != null ? String(policy.minContainers) : "",
  maxContainers:
    policy.maxContainers != null ? String(policy.maxContainers) : "",
  scaleUpStep: String(policy.scaleUpStep ?? ""),
  scaleDownStep: String(policy.scaleDownStep ?? ""),
  metricLookbackSeconds: String(policy.metricLookbackSeconds ?? ""),
  postScaleUpCooldownSeconds: String(policy.postScaleUpCooldownSeconds ?? ""),
  postScaleDownCooldownSeconds: String(
    policy.postScaleDownCooldownSeconds ?? "",
  ),
  postReleaseCooldownSeconds: String(policy.postReleaseCooldownSeconds ?? ""),
  useHorizontalScale: policy.useHorizontalScale ? "enabled" : "disabled",
});

const parseFormValues = (
  formValues: PolicyInputValues,
  base: ServiceSizingPolicyEditProps,
): ServiceSizingPolicyEditProps => {
  const intOrBase = (s: string, fallback: number): number => {
    const n = Number.parseInt(s, 10);
    return Number.isNaN(n) ? fallback : n;
  };
  const floatOrBase = (s: string, fallback: number): number => {
    const n = Number.parseFloat(s);
    return Number.isNaN(n) ? fallback : n;
  };
  return {
    ...base,
    percentile: floatOrBase(formValues.percentile, base.percentile),
    minimumMemory: intOrBase(formValues.minimumMemory, base.minimumMemory),
    maximumMemory: formValues.maximumMemory
      ? Number.parseInt(formValues.maximumMemory, 10)
      : null,
    minContainers: formValues.minContainers
      ? Number.parseInt(formValues.minContainers, 10)
      : null,
    maxContainers: formValues.maxContainers
      ? Number.parseInt(formValues.maxContainers, 10)
      : null,
    scaleUpStep: intOrBase(formValues.scaleUpStep, base.scaleUpStep),
    scaleDownStep: intOrBase(formValues.scaleDownStep, base.scaleDownStep),
    metricLookbackSeconds: intOrBase(
      formValues.metricLookbackSeconds,
      base.metricLookbackSeconds,
    ),
    postScaleUpCooldownSeconds: intOrBase(
      formValues.postScaleUpCooldownSeconds,
      base.postScaleUpCooldownSeconds,
    ),
    postScaleDownCooldownSeconds: intOrBase(
      formValues.postScaleDownCooldownSeconds,
      base.postScaleDownCooldownSeconds,
    ),
    postReleaseCooldownSeconds: intOrBase(
      formValues.postReleaseCooldownSeconds,
      base.postReleaseCooldownSeconds,
    ),
    minCpuThreshold: Number.parseFloat(formValues.minCpuThreshold),
    maxCpuThreshold: Number.parseFloat(formValues.maxCpuThreshold),
    memScaleUpThreshold: Number.parseFloat(formValues.memScaleUpThreshold),
    memScaleDownThreshold: Number.parseFloat(formValues.memScaleDownThreshold),
    memCpuRatioRThreshold: Number.parseFloat(formValues.memCpuRatioRThreshold),
    memCpuRatioCThreshold: Number.parseFloat(formValues.memCpuRatioCThreshold),
    useHorizontalScale: formValues.useHorizontalScale === "enabled",
  };
};

type ThresholdSettingsProps = {
  nextPolicy: ServiceSizingPolicyEditProps;
  errors: PolicyErrors;
  blurErrors: BlurErrors;
  inputValues: PolicyInputValues;
  updateInputValue: (field: keyof PolicyInputValues, value: string) => void;
  handleInputBlur: (
    field: keyof PolicyInputValues,
    min?: number,
    max?: number,
    decimalPlaces?: number,
    optional?: boolean,
    exclusiveMin?: boolean,
  ) => void;
};

const ThresholdSettings = ({
  nextPolicy,
  errors,
  blurErrors,
  inputValues,
  updateInputValue,
  handleInputBlur,
}: ThresholdSettingsProps) => {
  if (nextPolicy.autoscaling === "vertical") {
    return (
      <div className="flex flex-col gap-2">
        <h2 className="text-md text-gray-500">RAM & CPU Threshold Settings</h2>
        <FormGroup
          splitWidthInputs
          description="Percentile to use for RAM and CPU"
          label="Percentile"
          htmlFor="percentile"
          feedbackMessage={blurErrors.percentile || errors.percentile}
          feedbackVariant="danger"
        >
          <Input
            id="percentile"
            name="percentile"
            type="text"
            inputMode="decimal"
            value={inputValues.percentile}
            min="50"
            max="100"
            placeholder="50 (Min), 100 (Max)"
            onChange={(e) =>
              updateInputValue(
                "percentile",
                filterDecimalInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("percentile", 50, 100, 2)}
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          description="The minimum memory that vertical autoscaling can scale this service to"
          label="Minimum Memory (MB)"
          htmlFor="minimum-memory"
          feedbackMessage={blurErrors.minimumMemory}
          feedbackVariant="danger"
        >
          <Input
            id="minimum-memory"
            name="minimum-memory"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValues.minimumMemory}
            min="512"
            max="784384"
            placeholder="512 (Min), 784384 (Max)"
            onChange={(e) =>
              updateInputValue(
                "minimumMemory",
                filterIntegerInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("minimumMemory", 512, 784384, 0)}
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          description="The maximum memory that vertical autoscaling can scale this service to"
          label="Maximum Memory (MB)"
          htmlFor="maximum-memory"
          feedbackMessage={blurErrors.maximumMemory}
          feedbackVariant="danger"
        >
          <Input
            id="maximum-memory"
            name="maximum-memory"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValues.maximumMemory}
            min="512"
            max="784384"
            placeholder="512 (Min), 784384 (Max)"
            onChange={(e) =>
              updateInputValue(
                "maximumMemory",
                filterIntegerInput(e.currentTarget.value),
              )
            }
            onBlur={() =>
              handleInputBlur("maximumMemory", 512, 784384, 0, true)
            }
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          description="Percent of the current memory limit for a container that will trigger a scale up"
          label="Memory Scale Up Percentage"
          htmlFor="memory-scale-up"
          feedbackMessage={blurErrors.memScaleUpThreshold}
          feedbackVariant="danger"
        >
          <Input
            id="memory-scale-up"
            name="memory-scale-up"
            type="text"
            inputMode="decimal"
            step="0.01"
            value={inputValues.memScaleUpThreshold}
            min="0"
            max="1"
            placeholder="0 (Min), 1 (Max)"
            onChange={(e) =>
              updateInputValue(
                "memScaleUpThreshold",
                filterDecimalInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("memScaleUpThreshold", 0, 1, 2)}
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          description="Percent of the next smallest memory limit for a container that will trigger a scale down"
          label="Memory Scale Down Percentage"
          htmlFor="memory-scale-down"
          feedbackMessage={blurErrors.memScaleDownThreshold}
          feedbackVariant="danger"
        >
          <Input
            id="memory-scale-down"
            name="memory-scale-down"
            type="text"
            inputMode="decimal"
            step="0.01"
            value={inputValues.memScaleDownThreshold}
            min="0"
            max="1"
            placeholder="0 (Min), 1 (Max)"
            onChange={(e) =>
              updateInputValue(
                "memScaleDownThreshold",
                filterDecimalInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("memScaleDownThreshold", 0, 1, 2)}
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          feedbackMessage={blurErrors.memCpuRatioRThreshold || errors.ratios}
          feedbackVariant={
            blurErrors.memCpuRatioRThreshold || errors.ratios
              ? "danger"
              : "info"
          }
          description="Threshold of the Memory (in GB) to CPU (in CPUs) ratio in which values above will move into R profile"
          label="Memory Optimized Memory/CPU Ratio Threshold"
          htmlFor="r-ratio"
        >
          <Input
            id="r-ratio"
            name="r-ratio"
            type="text"
            inputMode="decimal"
            step="0.1"
            value={inputValues.memCpuRatioRThreshold}
            min="0"
            max="16"
            placeholder="0 (Min), 16 (Max)"
            onChange={(e) =>
              updateInputValue(
                "memCpuRatioRThreshold",
                filterDecimalInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("memCpuRatioRThreshold", 0, 16, 1)}
          />
        </FormGroup>
        <FormGroup
          splitWidthInputs
          description="Threshold of the Memory (in GB) to CPU (in CPUs) ratio in which values below will move into C profile"
          label="Compute Optimized Memory/CPU Ratio Threshold"
          htmlFor="c-ratio"
          feedbackMessage={blurErrors.memCpuRatioCThreshold}
          feedbackVariant="danger"
        >
          <Input
            id="c-ratio"
            name="c-ratio"
            type="text"
            inputMode="decimal"
            step="0.01"
            value={inputValues.memCpuRatioCThreshold}
            min="0"
            max="8"
            placeholder="0 (Min), 8 (Max)"
            onChange={(e) =>
              updateInputValue(
                "memCpuRatioCThreshold",
                filterDecimalInput(e.currentTarget.value),
              )
            }
            onBlur={() => handleInputBlur("memCpuRatioCThreshold", 0, 8, 2)}
          />
        </FormGroup>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-md text-gray-500">
        Container & CPU Threshold Settings
      </h2>
      <FormGroup
        splitWidthInputs
        description="Containers are scaled based on scale up steps, sequentially"
        feedbackMessage={
          blurErrors.minContainers ||
          errors.minContainers ||
          ((Number.parseInt(inputValues.minContainers, 10) || 0) < 2
            ? "Warning: High-availability requires at least 2 containers"
            : "")
        }
        feedbackVariant={
          blurErrors.minContainers || errors.minContainers ? "danger" : "warn"
        }
        label="Minimum Container Count"
        htmlFor="min-containers"
      >
        <Input
          id="min-containers"
          name="min-containers"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValues.minContainers}
          min="1"
          max="9999"
          placeholder=""
          onChange={(e) =>
            updateInputValue(
              "minContainers",
              filterIntegerInput(e.currentTarget.value),
            )
          }
          onBlur={() => handleInputBlur("minContainers", 1, 9999, 0)}
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="Containers are scaled based on scale up steps, sequentially"
        feedbackMessage={blurErrors.maxContainers || errors.maxContainers}
        feedbackVariant="danger"
        label="Maximum Container Count"
        htmlFor="max-containers"
      >
        <Input
          id="max-containers"
          name="max-containers"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValues.maxContainers}
          min="1"
          max="9999"
          placeholder=""
          onChange={(e) =>
            updateInputValue(
              "maxContainers",
              filterIntegerInput(e.currentTarget.value),
            )
          }
          onBlur={() => handleInputBlur("maxContainers", 1, 9999, 0)}
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="How many containers to increase by on an autoscale event"
        label="Scale Up Steps"
        htmlFor="scale-up-step"
        feedbackMessage={blurErrors.scaleUpStep || errors.scaleUpStep}
        feedbackVariant="danger"
      >
        <Input
          id="scale-up-step"
          name="scale-up-step"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValues.scaleUpStep}
          min="1"
          max="9999"
          placeholder=""
          onChange={(e) =>
            updateInputValue(
              "scaleUpStep",
              filterIntegerInput(e.currentTarget.value),
            )
          }
          onBlur={() => handleInputBlur("scaleUpStep", 1, 9999, 0)}
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="How many containers to decrease by on an autoscale event"
        label="Scale Down Steps"
        htmlFor="scale-down-step"
        feedbackMessage={blurErrors.scaleDownStep || errors.scaleDownStep}
        feedbackVariant="danger"
      >
        <Input
          id="scale-down-step"
          name="scale-down-step"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValues.scaleDownStep}
          min="1"
          max="9999"
          placeholder=""
          onChange={(e) =>
            updateInputValue(
              "scaleDownStep",
              filterIntegerInput(e.currentTarget.value),
            )
          }
          onBlur={() => handleInputBlur("scaleDownStep", 1, 9999, 0)}
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="Percent of CPU usage that will trigger a scale down"
        label="Scale Down Threshold (CPU Usage)"
        htmlFor="cpu-scale-down"
        feedbackMessage={blurErrors.minCpuThreshold || errors.minCpuThreshold}
        feedbackVariant="danger"
      >
        <Input
          id="cpu-scale-down"
          name="cpu-scale-down"
          type="text"
          inputMode="decimal"
          step="0.01"
          value={inputValues.minCpuThreshold}
          min="0"
          max="1"
          placeholder="0 (Min), 1 (Max)"
          onChange={(e) =>
            updateInputValue(
              "minCpuThreshold",
              filterDecimalInput(e.currentTarget.value),
            )
          }
          onBlur={() =>
            handleInputBlur("minCpuThreshold", 0, 1, 2, false, true)
          }
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="Percent of CPU usage that will trigger a scale up"
        label="Scale Up Threshold (CPU Usage)"
        htmlFor="cpu-scale-up"
        feedbackMessage={blurErrors.maxCpuThreshold || errors.maxCpuThreshold}
        feedbackVariant="danger"
      >
        <Input
          id="cpu-scale-up"
          name="cpu-scale-up"
          type="text"
          inputMode="decimal"
          step="0.01"
          value={inputValues.maxCpuThreshold}
          min="0"
          max="1"
          placeholder="0 (Min), 1 (Max)"
          onChange={(e) =>
            updateInputValue(
              "maxCpuThreshold",
              filterDecimalInput(e.currentTarget.value),
            )
          }
          onBlur={() =>
            handleInputBlur("maxCpuThreshold", 0, 1, 2, false, true)
          }
        />
      </FormGroup>
      <FormGroup
        splitWidthInputs
        description="Percentile to use for CPU"
        label="Percentile"
        htmlFor="percentile"
        feedbackMessage={blurErrors.percentile || errors.percentile}
        feedbackVariant="danger"
      >
        <Input
          id="percentile"
          name="percentile"
          type="text"
          inputMode="decimal"
          value={inputValues.percentile}
          min="50"
          max="100"
          placeholder="50 (Min), 100 (Max)"
          onChange={(e) =>
            updateInputValue(
              "percentile",
              filterDecimalInput(e.currentTarget.value),
            )
          }
          onBlur={() => handleInputBlur("percentile", 50, 100, 2)}
        />
      </FormGroup>
    </div>
  );
};

type AdvancedDisclosureProps = ThresholdSettingsProps & {
  autoscalingType: AutoscalingTypeInp;
  advancedIsOpen: boolean;
  onToggle: () => void;
  resetAdvancedSettings: () => void;
};

const AdvancedDisclosure = ({
  autoscalingType,
  advancedIsOpen,
  onToggle,
  resetAdvancedSettings,
  nextPolicy,
  errors,
  blurErrors,
  inputValues,
  updateInputValue,
  handleInputBlur,
}: AdvancedDisclosureProps) => {
  if (autoscalingType === "disabled") return null;
  return (
    <Disclosure
      labelWhenClosed="Show Advanced Settings"
      labelWhenOpen="Hide Advanced Settings"
      open={advancedIsOpen}
      onToggle={onToggle}
    >
      <div className="pb-4">
        <div className="flex flex-col gap-2">
          <ThresholdSettings
            nextPolicy={nextPolicy}
            errors={errors}
            blurErrors={blurErrors}
            inputValues={inputValues}
            updateInputValue={updateInputValue}
            handleInputBlur={handleInputBlur}
          />
          <h2 className="text-md text-gray-500">Time-Based Settings</h2>
          <FormGroup
            splitWidthInputs
            description="Time interval in seconds to fetch metrics for evaluation"
            label="Metrics Lookback Time Interval"
            htmlFor="lookback-interval"
            feedbackMessage={
              blurErrors.metricLookbackSeconds || errors.metricLookbackSeconds
            }
            feedbackVariant="danger"
          >
            <Input
              id="lookback-interval"
              name="lookback-interval"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValues.metricLookbackSeconds}
              min="130"
              max="3600"
              placeholder="130 (Min), 3600 (Max)"
              onChange={(e) =>
                updateInputValue(
                  "metricLookbackSeconds",
                  filterIntegerInput(e.currentTarget.value),
                )
              }
              onBlur={() =>
                handleInputBlur("metricLookbackSeconds", 130, 3600, 0)
              }
            />
          </FormGroup>
          <FormGroup
            splitWidthInputs
            description="The number of seconds to wait after an automated scale to potentially scale up"
            label="Scale Up Cooldown"
            htmlFor="scale-up-cooldown"
            feedbackMessage={blurErrors.postScaleUpCooldownSeconds}
            feedbackVariant="danger"
          >
            <Input
              id="scale-up-cooldown"
              name="scale-up-cooldown"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValues.postScaleUpCooldownSeconds}
              min="0"
              max="3600"
              placeholder="0 (Min), 3600 (Max)"
              onChange={(e) =>
                updateInputValue(
                  "postScaleUpCooldownSeconds",
                  filterIntegerInput(e.currentTarget.value),
                )
              }
              onBlur={() =>
                handleInputBlur("postScaleUpCooldownSeconds", 0, 3600, 0)
              }
            />
          </FormGroup>
          <FormGroup
            splitWidthInputs
            description="The number of seconds to wait after an automated scale to potentially scale down"
            label="Scale Down Cooldown"
            htmlFor="scale-down-cooldown"
            feedbackMessage={blurErrors.postScaleDownCooldownSeconds}
            feedbackVariant="danger"
          >
            <Input
              id="scale-down-cooldown"
              name="scale-down-cooldown"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValues.postScaleDownCooldownSeconds}
              min="0"
              max="3600"
              placeholder="0 (Min), 3600 (Max)"
              onChange={(e) =>
                updateInputValue(
                  "postScaleDownCooldownSeconds",
                  filterIntegerInput(e.currentTarget.value),
                )
              }
              onBlur={() =>
                handleInputBlur("postScaleDownCooldownSeconds", 0, 3600, 0)
              }
            />
          </FormGroup>
          <FormGroup
            splitWidthInputs
            description="The number of seconds ignore in metrics after a scale event to allow for service stabilization"
            label="Post Release Cooldown"
            htmlFor="release-cooldown"
            feedbackMessage={blurErrors.postReleaseCooldownSeconds}
            feedbackVariant="danger"
          >
            <Input
              id="release-cooldown"
              name="release-cooldown"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValues.postReleaseCooldownSeconds}
              min="0"
              max="3600"
              placeholder="0 (Min), 3600 (Max)"
              onChange={(e) =>
                updateInputValue(
                  "postReleaseCooldownSeconds",
                  filterIntegerInput(e.currentTarget.value),
                )
              }
              onBlur={() =>
                handleInputBlur("postReleaseCooldownSeconds", 0, 3600, 0)
              }
            />
          </FormGroup>
          <h2 className="text-md text-gray-500">General Settings</h2>
          {nextPolicy.autoscaling === "horizontal" ? (
            <FormGroup
              splitWidthInputs
              description="Incremental Replacement only adds or removes the exact number of containers needed to reach your desired total. Full Replacement will always replace all existing containers."
              label="Container Replacement Strategy"
              htmlFor="horizontal-scale"
            >
              <Select
                id="horizontal-scale"
                ariaLabel="Container Replacement Strategy"
                value={inputValues.useHorizontalScale}
                onSelect={(opt) =>
                  updateInputValue("useHorizontalScale", opt.value)
                }
                options={[
                  {
                    label: "Incremental Replacement (recommended)",
                    value: "enabled",
                  },
                  { label: "Full Replacement", value: "disabled" },
                ]}
              />
            </FormGroup>
          ) : null}
          <FormGroup
            splitWidthInputs
            label="Reset Advanced Settings to Defaults"
            description="This will restore settings to their default values."
            htmlFor="reset-button"
          >
            <div id="reset-button">
              <ButtonIcon
                icon={<IconRefresh variant="sm" />}
                variant="white"
                onClick={resetAdvancedSettings}
              >
                Reset to Defaults
              </ButtonIcon>
            </div>
          </FormGroup>
        </div>
      </div>
    </Disclosure>
  );
};

const AutoscalingSection = ({
  serviceId,
  stackId,
}: { serviceId: string; stackId: string }) => {
  const dispatch = useDispatch();
  useQuery(fetchServiceSizingPoliciesByServiceId({ serviceId }));
  const existingPolicy = useSelector((s) =>
    selectServiceSizingPolicyByServiceId(s, { id: serviceId }),
  );
  const [nextPolicy, setNextPolicy] = useState(existingPolicy);
  useEffect(() => {
    setNextPolicy(existingPolicy);
  }, [existingPolicy.id]);

  const [inputValues, setInputValues] = useState(() =>
    computeInitialInputValues(existingPolicy as ServiceSizingPolicyEditProps),
  );

  const [blurErrors, setBlurErrors] = useState<BlurErrors>({});

  useEffect(() => {
    setInputValues(
      computeInitialInputValues(existingPolicy as ServiceSizingPolicyEditProps),
    );
    setBlurErrors({});
  }, [existingPolicy.id]);
  const [autoscalingType, setAutoscalingType] =
    useState<AutoscalingTypeInp>("disabled");
  useEffect(() => {
    if (existingPolicy.scalingEnabled)
      setAutoscalingType(existingPolicy.autoscaling);
    else setAutoscalingType("disabled");
  }, [existingPolicy.autoscaling, existingPolicy.scalingEnabled]);
  const getChangesExist = () => {
    if (!nextPolicy.scalingEnabled && !existingPolicy.scalingEnabled) {
      return false;
    }
    if (nextPolicy.scalingEnabled !== existingPolicy.scalingEnabled)
      return true;
    if (nextPolicy.autoscaling !== existingPolicy.autoscaling) return true;
    const initial = computeInitialInputValues(
      existingPolicy as ServiceSizingPolicyEditProps,
    );
    return (Object.keys(initial) as (keyof PolicyInputValues)[]).some(
      (k) => inputValues[k] !== initial[k],
    );
  };
  const changesExist = getChangesExist();

  const modifyLoader = useLoader(modifyServiceSizingPolicy);
  const stack = useSelector((s) => selectStackById(s, { id: stackId }));
  const orgId = useSelector(selectOrganizationSelectedId);
  const activePlan = useSelector(selectFirstActivePlan);

  useQuery(fetchActivePlans({ orgId }));

  const [errors, validate] = useValidator<
    ServiceSizingPolicyEditProps,
    typeof policyValidators
  >(policyValidators);
  const onSubmitForm = (e: SyntheticEvent) => {
    e.preventDefault();
    if (Object.keys(blurErrors).length > 0) return;
    const parsed = parseFormValues(inputValues, nextPolicy);
    if (!validate(parsed)) return;
    dispatch(
      modifyServiceSizingPolicy({ ...parsed, id: nextPolicy.id, serviceId }),
    );
  };
  const updatePolicy = <K extends keyof ServiceSizingPolicyEditProps>(
    key: K,
    value: ServiceSizingPolicyEditProps[K],
  ) => {
    setNextPolicy((lastPolicy) => {
      return { ...lastPolicy, [key]: value };
    });
  };

  const updateInputValue = (field: keyof PolicyInputValues, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
    setBlurErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleInputBlur = (
    field: keyof PolicyInputValues,
    min = 0,
    max = 1,
    decimalPlaces = 2,
    optional = false,
    exclusiveMin = false,
  ) => {
    const value = inputValues[field];
    const parsed = Number.parseFloat(value);
    if (value === "" || Number.isNaN(parsed)) {
      if (value === "" && optional) {
        setBlurErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      } else if (value === "") {
        setBlurErrors((prev) => ({
          ...prev,
          [field]: "This field cannot be empty",
        }));
      } else {
        setBlurErrors((prev) => ({
          ...prev,
          [field]: "Please enter a valid number",
        }));
      }
      return;
    }
    const belowMin = exclusiveMin ? parsed <= min : parsed < min;
    if (belowMin || parsed > max) {
      setBlurErrors((prev) => ({
        ...prev,
        [field]: `Please enter a value between ${min} and ${max}`,
      }));
      return;
    }
    setBlurErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    const clamped = Math.max(min, Math.min(max, parsed));
    setInputValues((prev) => ({
      ...prev,
      [field]: Number(clamped.toFixed(decimalPlaces)).toFixed(decimalPlaces),
    }));
  };

  const resetAdvancedSettings = () => {
    const resetPolicy = {
      ...schema.serviceSizingPolicies.empty,
      id: nextPolicy.id,
      scalingEnabled: nextPolicy.scalingEnabled,
      autoscaling: nextPolicy.autoscaling,
    };
    setNextPolicy(resetPolicy);
    setInputValues(computeInitialInputValues(resetPolicy));
    setBlurErrors({});
  };

  const [advancedIsOpen, setOpen] = useState(false);

  const horizontalAutoscalingEnabled =
    stack.horizontalAutoscaling || activePlan.horizontalAutoscaling;
  const verticalAutoscalingEnabled =
    stack.verticalAutoscaling || activePlan.verticalAutoscaling;

  const options: SelectOption[] = [
    {
      label: "Disabled: No autoscaling",
      value: "disabled",
    },
  ];

  if (horizontalAutoscalingEnabled) {
    options.push({
      label: "Enabled: Horizontal Autoscaling",
      value: "horizontal",
    });
  }

  if (verticalAutoscalingEnabled) {
    options.push({
      label: "Enabled: Vertical Autoscaling",
      value: "vertical",
    });
  }

  const autoscalingDescriptions = {
    horizontal:
      "Automatically scale your services by regularly reviewing recent CPU utilization and scale to the optimal container count.",
    vertical:
      "Automatically scale your services by regularly reviewing recent CPU and RAM utilization and scale to the optimal configuration.",
  };

  const setAutoscaling = (opt: SelectOption<AutoscalingTypeInp> | null) => {
    if (!opt) return;
    setOpen(false);

    if (opt.value === "disabled") {
      updatePolicy("scalingEnabled", false);
      setAutoscalingType("disabled");
      return;
    }

    updatePolicy("scalingEnabled", true);
    updatePolicy("autoscaling", opt.value);

    // Reset conflicting fields when switching between HAS/VAS modes
    if (opt.value === "horizontal") {
      // Switching to HAS: ensure useHorizontalScale is true
      updatePolicy("useHorizontalScale", true);
    } else if (opt.value === "vertical") {
      // Switching to VAS: reset HAS-specific fields to defaults to prevent conflicts
      updatePolicy("useHorizontalScale", false);
      updatePolicy("minContainers", 2);
      updatePolicy("maxContainers", 4);
      updatePolicy("minCpuThreshold", 0.1);
      updatePolicy("maxCpuThreshold", 0.9);
      updatePolicy("scaleUpStep", 1);
      updatePolicy("scaleDownStep", 1);
    }

    setAutoscalingType(opt.value);

    if (opt.value === "horizontal") setOpen(true);
  };

  if (!verticalAutoscalingEnabled && !horizontalAutoscalingEnabled) {
    return null;
  }

  return (
    <Box>
      <form onSubmit={onSubmitForm}>
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start">
            <h1 className="text-lg text-gray-500">Autoscale</h1>
            <ButtonLinkDocs href="https://www.aptible.com/docs/core-concepts/scaling/app-scaling#autoscaling" />
          </div>
          <BannerMessages {...modifyLoader} />
          <div className="flex gap-4">
            <FormGroup
              label="Autoscaling"
              htmlFor="autoscaling"
              className="w-1/2"
            >
              <Select
                ariaLabel="Autoscaling Setting"
                id="autoscaling"
                options={options}
                onSelect={(opt) =>
                  setAutoscaling(opt as SelectOption<AutoscalingTypeInp>)
                }
                value={autoscalingType}
              />
              <p className="mt-2 text-gray-500">
                {nextPolicy.scalingEnabled
                  ? autoscalingDescriptions[nextPolicy.autoscaling]
                  : ""}
              </p>
            </FormGroup>
            <div className="w-1/2 pb-4">
              <PolicySummary policy={existingPolicy} title="Current" />
            </div>
          </div>
          <div>
            <AdvancedDisclosure
              autoscalingType={autoscalingType}
              advancedIsOpen={advancedIsOpen}
              onToggle={() => setOpen(!advancedIsOpen)}
              nextPolicy={nextPolicy}
              errors={errors}
              blurErrors={blurErrors}
              inputValues={inputValues}
              updateInputValue={updateInputValue}
              handleInputBlur={handleInputBlur}
              resetAdvancedSettings={resetAdvancedSettings}
            />
          </div>
          <hr />

          {Object.keys(blurErrors).length > 0 ? (
            <p className="text-red-500 text-sm mb-2">
              Please correct values in the invalid fields before continuing.
            </p>
          ) : null}
          <div className="flex">
            <Button
              name="autoscaling"
              className="w-40 flex font-semibold"
              type="submit"
              disabled={!changesExist || Object.keys(blurErrors).length > 0}
              isLoading={modifyLoader.isLoading}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </Box>
  );
};

export const AppDetailServiceScalePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id = "", serviceId = "" } = useParams();
  const [errors, validate] = useValidator<AppScaleProps, typeof validators>(
    validators,
  );
  useQuery(fetchApp({ id }));
  const [containerCount, setContainerCount] = useState(1);
  const [containerProfileType, setContainerProfileType] =
    useState<InstanceClass>(DEFAULT_INSTANCE_CLASS);
  const [containerSize, setContainerSize] = useState<number>(512);
  const [takingRecommendation, setTakingRecommendation] = useState(false);
  const app = useSelector((s) => selectAppById(s, { id }));
  useQuery(fetchService({ id: serviceId }));
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const stack = useSelector((s) =>
    selectStackById(s, { id: environment.stackId }),
  );
  const containerProfilesForStack = useSelector((s) =>
    selectContainerProfilesForStack(s, { id: environment.stackId }),
  );
  const endpoints = useSelector((s) =>
    selectEndpointsByServiceId(s, { serviceId }),
  );
  const recommendation = useSelector((s) =>
    selectManualScaleRecommendationByServiceId(s, { serviceId: serviceId }),
  );
  const activeDiskMountPoints = useSelector((s) =>
    selectActiveMountPointsByServiceId(s, { serviceId }),
  );
  const hasDiskAttached = activeDiskMountPoints.length > 0;
  const persistentDiskCost = useSelector((s) =>
    selectPersistentDiskCostByService(s, { serviceId }),
  );

  const action = scaleService({
    id: serviceId,
    containerCount,
    containerSize,
    containerProfile: containerProfileType,
    recId: takingRecommendation ? recommendation.id : "",
  });
  const onSubmitForm = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validate({ containerCount })) return;
    dispatch(action);
  };
  const loader = useLoader(scaleService);

  useEffect(() => {
    setContainerCount(service.containerCount);
  }, [service.containerCount]);

  useEffect(() => {
    setContainerProfileType(service.instanceClass);
  }, [service.instanceClass]);

  useEffect(() => {
    if (service.containerMemoryLimitMb) {
      setContainerSize(service.containerMemoryLimitMb);
    }
  }, [service.containerMemoryLimitMb]);

  const changesExist =
    service.containerCount !== containerCount ||
    service.instanceClass !== containerProfileType ||
    service.containerMemoryLimitMb !== containerSize;

  useLoaderSuccess(loader, () => {
    navigate(appActivityUrl(app.id));
  });
  const currentContainerProfile = getContainerProfileFromType(
    service.instanceClass,
  );
  const requestedContainerProfile =
    getContainerProfileFromType(containerProfileType);

  const profileOptions = Object.keys(containerProfilesForStack).map(
    (profileType) => {
      const profile = getContainerProfileFromType(profileType as InstanceClass);
      return {
        label: profile.name,
        value: profileType,
      };
    },
  );

  const memoryContainerOptions = containerSizesByProfile(
    containerProfileType,
  ).map((containerSizeOption) => {
    return {
      label: `${containerSizeOption / 1024} GB`,
      value: `${containerSizeOption}`,
    };
  });

  const changeContainerProfile = (opt: SelectOption | null) => {
    if (!opt) return;
    const instClass = opt.value as InstanceClass;
    const profile = containerProfilesForStack[instClass];
    if (!profile) {
      return;
    }

    if (containerSize < profile.minimumContainerSize) {
      setContainerSize(profile.minimumContainerSize);
    }
    setContainerProfileType(instClass);
  };

  const cancelScale = () => {
    setContainerProfileType(service.instanceClass);
    setContainerSize(service.containerMemoryLimitMb);
    setContainerCount(service.containerCount);
    setTakingRecommendation(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <LastScaleBanner serviceId={serviceId} />
      <ContainerEfficiencySection
        serviceId={serviceId}
        onAcceptRecommendation={() => {
          setContainerProfileType(
            recommendation.recommendedInstanceClass as InstanceClass,
          );
          setContainerSize(recommendation.recommendedContainerMemoryLimitMb);
          setTakingRecommendation(true);
        }}
      />

      <AutoscalingSection serviceId={serviceId} stackId={stack.id} />

      <Box>
        <form onSubmit={onSubmitForm}>
          <div className="flex flex-col gap-2">
            <h1 className="text-lg text-gray-500 mb-4">Manual Scale</h1>

            <FormGroup
              splitWidthInputs
              description="Optimize container performance with a custom profile."
              label="Container Profile"
              htmlFor="container-profile"
            >
              <Select
                id="container-profile"
                ariaLabel="container-profile"
                disabled={Object.keys(containerProfilesForStack).length <= 1}
                value={containerProfileType}
                onSelect={changeContainerProfile}
                options={profileOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description={`Horizontally scale this service by increasing the number of containers. A count of 2 or more will provide High Availability. 32 max count for ${
                getContainerProfileFromType(containerProfileType).name
              } profiles.`}
              label="Number of Containers"
              htmlFor="number-containers"
              feedbackMessage={errors.containerCount}
              feedbackVariant={errors.containerCount ? "danger" : "info"}
            >
              <Input
                id="number-containers"
                name="number-containers"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={containerCount}
                min="0"
                max="32"
                placeholder="0 (Min), 32 (Max)"
                onChange={(e) =>
                  setContainerCount(
                    Number.parseInt(e.currentTarget.value || "0", 10),
                  )
                }
              />
            </FormGroup>

            {hasDiskAttached && containerCount > 1 ? (
              <Banner variant="error">
                Cannot scale to more than 1 container when a disk is attached.
                Detach the disk to proceed.
              </Banner>
            ) : null}
            <FormGroup
              splitWidthInputs
              description="Specify the memory you wish to allow per container."
              label="Memory per Container"
              htmlFor="memory-container"
            >
              <Select
                id="memory-container"
                value={containerSize.toString()}
                onSelect={(opt) => setContainerSize(Number.parseInt(opt.value))}
                options={memoryContainerOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description="CPU Share is determined by the selected Memory Limit and Container Profile."
              label="CPU Share per Container"
              htmlFor="cpu-share"
            >
              <Input
                id="number-containers"
                name="number-containers"
                className="disabled"
                type="text"
                disabled
                value={requestedContainerProfile.cpuShare * containerSize}
              />
            </FormGroup>
          </div>

          <ServicePricingCalc
            service={service}
            endpoints={endpoints}
            persistentDiskCost={persistentDiskCost}
          />

          <hr />

          {changesExist ? (
            <div className="text-md font-semibold text-gray-900 mt-4">
              Pending Changes
            </div>
          ) : null}
          {containerProfileType !== service.instanceClass ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Profile</div>
              <p className="text-black-500">
                Changed from {currentContainerProfile.name} to{" "}
                {requestedContainerProfile.name}
              </p>
            </div>
          ) : null}
          {containerCount !== service.containerCount ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Count</div>
              <p className="text-black-500">
                Changed from {service.containerCount} to {containerCount}
              </p>
            </div>
          ) : null}
          {containerSize !== service.containerMemoryLimitMb ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Size</div>
              <p className="text-black-500" id="container-size-txt">
                Changed from {service.containerMemoryLimitMb / 1024} GB to{" "}
                {containerSize / 1024} GB
              </p>
            </div>
          ) : null}
          {changesExist ? (
            <ServicePricingCalc
              service={{
                containerCount,
                containerMemoryLimitMb: containerSize,
                instanceClass: containerProfileType,
              }}
              endpoints={endpoints}
              persistentDiskCost={persistentDiskCost}
            />
          ) : null}

          <BannerMessages {...loader} />

          <div className="flex mt-4">
            <Button
              className="w-40 flex font-semibold"
              type="submit"
              disabled={!changesExist}
              isLoading={loader.isLoading}
            >
              Save Changes
            </Button>

            {changesExist ? (
              <Button
                className="w-40 ml-2 flex font-semibold"
                onClick={cancelScale}
                variant="white"
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      </Box>
    </div>
  );
};
